import {
	ContractorDetailsItem,
	ContractorListItem,
	GetContractorsQueryParams,
	PaginatedResponse,
} from "@dune/types";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { contractorsQueryKeys } from "../query-keys/contractors.query-keys";

const getContractors = async (
	params: GetContractorsQueryParams,
): Promise<PaginatedResponse<ContractorListItem>> => {
	const searchParams = new URLSearchParams();
	if (params.search) searchParams.append("search", params.search);
	if (params.category) searchParams.append("category", params.category);
	if (params.type) searchParams.append("type", params.type);
	if (params.insurer) searchParams.append("insurer", params.insurer);
	if (params.page) searchParams.append("page", String(params.page));
	if (params.limit) searchParams.append("limit", String(params.limit));

	const { data } = await privateAxios.get<
		PaginatedResponse<ContractorListItem>
	>(`/contractors?${searchParams.toString()}`);
	return data;
};

const getContractorById = async (
	id: number,
): Promise<ContractorDetailsItem> => {
	const { data } = await privateAxios.get<ContractorDetailsItem>(
		`/contractors/${id}`,
	);
	return data;
};

export const useGetContractorsList = (params: GetContractorsQueryParams) => {
	return useQuery({
		queryKey: contractorsQueryKeys.list(
			params.search,
			params.category,
			params.type,
			params.insurer,
			params.page,
			params.limit,
		),
		queryFn: () => getContractors(params),
		placeholderData: keepPreviousData,
	});
};

export const useGetContractorById = (id: number) => {
	return useQuery({
		queryKey: contractorsQueryKeys.contractorById(id),
		queryFn: () => getContractorById(id),
		enabled: !!id,
	});
};

export const updateParticipantInsurer = async (
	participantId: number,
	constructionSiteId: number,
	insurerSocietyId: number,
): Promise<void> => {
	await privateAxios.post(
		`/contractors/${participantId}/construction-sites/${constructionSiteId}/insurer`,
		{ insurerSocietyId },
	);
};

export const useUpdateParticipantInsurer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			participantId,
			constructionSiteId,
			insurerSocietyId,
		}: {
			participantId: number;
			constructionSiteId: number;
			insurerSocietyId: number;
		}) =>
			updateParticipantInsurer(
				participantId,
				constructionSiteId,
				insurerSocietyId,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: contractorsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				predicate: (query) => {
					const key = query.queryKey;
					return (
						Array.isArray(key) &&
						key.includes("construction-site-submission") &&
						(key.includes("reception") ||
							key.includes("participants-reception") ||
							key.includes("participants"))
					);
				},
			});
		},
	});
};
